<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .checkin-page
    //- Header
    .page-header
      button(@click="router.back()" type="button").back-button
        img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        span Check-In
      .checkin-summary(v-if="admissions.length")
        span.summary-count {{ checkedInCount }} / {{ admissions.length }} checked in
        .progress-bar
          .progress-fill(:style="{ width: progressPercent + '%' }")

    //- Manual code entry
    .code-entry-section
      .code-entry-label Enter or scan ticket code
      .code-entry-row
        el-input(
          v-model="codeInput"
          placeholder="Ticket code..."
          @keyup.enter="handleManualCheckIn"
          clearable
          size="large"
        )
          template(#append)
            el-button(@click="handleManualCheckIn" :loading="checkingIn" type="primary") Check In

    //- Filter tabs
    .filter-tabs
      button(
        v-for="filter in filters"
        :key="filter.value"
        @click="activeFilter = filter.value"
        :class="{ active: activeFilter === filter.value }"
        type="button"
      ).filter-tab {{ filter.label }}

    //- Search
    .search-row
      el-input(v-model="searchQuery" placeholder="Search attendees..." clearable)
        template(#prefix)
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon

    //- Attendee list
    .admissions-list(v-loading="loading")
      .admission-row(
        v-for="admission in filteredAdmissions"
        :key="admission.id"
        :class="{ 'is-checked-in': admission.checkedInAt }"
      )
        .admission-info
          .admission-tier {{ admission.tierName }}
          .admission-option(v-if="admission.selectedOption") {{ admission.selectedOption }}
          .admission-seat(v-if="admission.section || admission.row || admission.seat")
            span(v-if="admission.section") Sec {{ admission.section }}
            span(v-if="admission.row") &nbsp;· Row {{ admission.row }}
            span(v-if="admission.seat") &nbsp;· Seat {{ admission.seat }}
          .admission-code {{ admission.code.slice(0, 12) }}...
        .admission-status
          .status-checked(v-if="admission.checkedInAt")
            img(src="@/assets/icons/check.svg" alt="Checked in").status-icon
            span.status-time {{ formatTime(admission.checkedInAt) }}
          el-button(
            v-else
            size="small"
            type="primary"
            @click="checkInAdmission(admission)"
            :loading="admission._checkingIn"
          ) Check In

    //- Empty state
    .empty-state(v-if="!loading && !admissions.length")
      .empty-icon 🎟
      h3.empty-title No attendees found
      p.empty-subtitle No admissions have been issued for this event yet.

    //- No results from filter/search
    .empty-state(v-else-if="!loading && admissions.length && !filteredAdmissions.length")
      p.empty-subtitle No attendees match your search or filter.
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import dayjs from 'dayjs';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { findStickerRsvps, checkInIssuedTicket } from '@/graphs/gql';

interface IssuedTicket {
  id: string;
  code: string;
  tierName: string;
  selectedOption?: string;
  stickerId: string;
  variantId: string;
  section?: string;
  row?: string;
  seat?: string;
  checkedInAt?: string;
  createdAt: string;
  _checkingIn?: boolean;
}

export default defineComponent({
  name: 'HostCheckIn',
  components: { WebAppLayout },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const stickerId = String(route.params.stickerId);
    const loading = ref(false);
    const checkingIn = ref(false);
    const codeInput = ref('');
    const searchQuery = ref('');
    const activeFilter = ref<'all' | 'checkedIn' | 'notCheckedIn'>('all');

    const admissions = ref<IssuedTicket[]>([]);

    const filters = [
      { label: 'All', value: 'all' },
      { label: 'Checked In', value: 'checkedIn' },
      { label: 'Not Checked In', value: 'notCheckedIn' },
    ];

    const checkedInCount = computed(() => admissions.value.filter((a) => a.checkedInAt).length);

    const progressPercent = computed(() => (admissions.value.length
      ? Math.round((checkedInCount.value / admissions.value.length) * 100)
      : 0));

    const filteredAdmissions = computed(() => {
      let list = admissions.value;
      if (activeFilter.value === 'checkedIn') list = list.filter((a) => a.checkedInAt);
      if (activeFilter.value === 'notCheckedIn') list = list.filter((a) => !a.checkedInAt);
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(
          (a) => a.tierName.toLowerCase().includes(q)
            || a.code.toLowerCase().includes(q)
            || a.selectedOption?.toLowerCase().includes(q),
        );
      }
      return list;
    });

    async function fetchAdmissions() {
      loading.value = true;
      try {
        const response = await makeRequest(
          'query',
          findStickerRsvps,
          { stickerId },
          null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        admissions.value = response?.data?.findStickerRsvps || [];
      } catch (err: any) {
        console.error('Failed to load admissions:', err);
        ElMessage.error('Failed to load attendees');
      } finally {
        loading.value = false;
      }
    }

    async function doCheckIn(code: string): Promise<boolean> {
      const response = await makeRequest(
        'mutation',
        checkInIssuedTicket,
        { code },
        null,
      );
      if (response?.errors) throw new Error(response.errors[0]?.message);
      return response?.data?.checkInIssuedTicket === true;
    }

    async function checkInAdmission(admission: IssuedTicket) {
      admission._checkingIn = true;
      try {
        const success = await doCheckIn(admission.code);
        if (success) {
          admission.checkedInAt = new Date().toISOString();
          ElMessage.success('Checked in!');
        } else {
          ElMessage.error('Check-in failed — code not recognised');
        }
      } catch (err: any) {
        ElMessage.error(err.message || 'Check-in failed');
      } finally {
        admission._checkingIn = false;
      }
    }

    async function handleManualCheckIn() {
      const code = codeInput.value.trim();
      if (!code) return;
      checkingIn.value = true;
      try {
        const success = await doCheckIn(code);
        if (success) {
          const row = admissions.value.find((a) => a.code === code);
          if (row) row.checkedInAt = new Date().toISOString();
          ElMessage.success('Checked in!');
          codeInput.value = '';
        } else {
          ElMessage.error('Ticket code not found');
        }
      } catch (err: any) {
        ElMessage.error(err.message || 'Check-in failed');
      } finally {
        checkingIn.value = false;
      }
    }

    function formatTime(date: string) {
      return dayjs(date).format('h:mm A');
    }

    onMounted(fetchAdmissions);

    return {
      router,
      loading,
      checkingIn,
      codeInput,
      searchQuery,
      activeFilter,
      filters,
      admissions,
      filteredAdmissions,
      checkedInCount,
      progressPercent,
      checkInAdmission,
      handleManualCheckIn,
      formatTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.checkin-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 1rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
  opacity: 0.6;
}

.checkin-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  min-width: 0;
}

.summary-count {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
  white-space: nowrap;
}

.progress-bar {
  width: 120px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  border-radius: 999px;
  transition: width 0.4s ease;
}

.code-entry-section {
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.code-entry-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.6rem;
}

.code-entry-row {
  display: flex;
  gap: 0.75rem;
}

.filter-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.filter-tab {
  padding: 0.45rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &.active {
    border-color: #0d9488;
    background: #0d9488;
    color: #fff;
  }

  &:hover:not(.active) {
    border-color: #9ca3af;
    color: #1a1a1a;
  }
}

.search-row {
  padding: 0.75rem 1.5rem;

  .search-icon {
    width: 1rem;
    height: 1rem;
    opacity: 0.5;
  }
}

.admissions-list {
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-bottom: 1rem;
}

.admission-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  gap: 0.75rem;
  transition: border-color 0.2s, background 0.2s;

  &.is-checked-in {
    border-color: #99f6e4;
    background: #f0fdf9;
  }
}

.admission-info {
  min-width: 0;
  flex: 1;
}

.admission-tier {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.admission-option {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 2px;
}

.admission-seat {
  font-size: 0.78rem;
  color: #555;
  margin-bottom: 2px;
}

.admission-code {
  font-size: 0.72rem;
  font-family: monospace;
  color: #9ca3af;
  margin-top: 2px;
}

.admission-status {
  flex-shrink: 0;
}

.status-checked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: #0d9488;
}

.status-icon {
  width: 20px;
  height: 20px;
}

.status-time {
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.empty-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}
</style>
